import type { StoredTokens } from './types';

// localStorage + BroadcastChannel so a login or logout in one tab is
// reflected in the others (same pattern as the WAF portal).

const KEYS = {
  accessToken: 'semmco-eebd.auth.access_token',
  idToken: 'semmco-eebd.auth.id_token',
  refreshToken: 'semmco-eebd.auth.refresh_token',
  tokenType: 'semmco-eebd.auth.token_type',
  tokenExpiry: 'semmco-eebd.auth.token_expiry',
} as const;

const CHANNEL_NAME = 'semmco-eebd.auth';
const UPDATED_EVENT = 'semmco-eebd.auth.tokens_updated';

let channel: BroadcastChannel | null = null;

function getChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') return null;
  if (!channel) channel = new BroadcastChannel(CHANNEL_NAME);
  return channel;
}

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function storeTokens(tokens: Omit<StoredTokens, 'tokenExpiry'> & { expiresIn: number }): void {
  const expiry = Date.now() + tokens.expiresIn * 1000;
  try {
    localStorage.setItem(KEYS.accessToken, tokens.accessToken);
    localStorage.setItem(KEYS.idToken, tokens.idToken);
    localStorage.setItem(KEYS.refreshToken, tokens.refreshToken);
    localStorage.setItem(KEYS.tokenType, tokens.tokenType);
    localStorage.setItem(KEYS.tokenExpiry, expiry.toString());
  } catch {
    /* storage unavailable — session lasts for this page only */
  }
  getChannel()?.postMessage({ type: 'tokens_updated' });
  window.dispatchEvent(new CustomEvent(UPDATED_EVENT));
}

export function getStoredTokens(): StoredTokens | null {
  const accessToken = read(KEYS.accessToken);
  const expiry = read(KEYS.tokenExpiry);
  if (!accessToken || !expiry) return null;
  return {
    accessToken,
    idToken: read(KEYS.idToken) || accessToken,
    refreshToken: read(KEYS.refreshToken) || '',
    tokenType: read(KEYS.tokenType) || 'Bearer',
    tokenExpiry: Number.parseInt(expiry, 10),
  };
}

export function clearTokens(): void {
  try {
    Object.values(KEYS).forEach((k) => localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
  getChannel()?.postMessage({ type: 'tokens_cleared' });
  window.dispatchEvent(new CustomEvent(UPDATED_EVENT));
}

export function isTokenExpired(skewSeconds = 30): boolean {
  const tokens = getStoredTokens();
  if (!tokens) return true;
  return Date.now() + skewSeconds * 1000 >= tokens.tokenExpiry;
}

export function subscribeToTokenChanges(callback: () => void): () => void {
  const ch = getChannel();
  const handler = () => callback();
  window.addEventListener(UPDATED_EVENT, handler);
  ch?.addEventListener('message', handler);
  return () => {
    window.removeEventListener(UPDATED_EVENT, handler);
    ch?.removeEventListener('message', handler);
  };
}
